import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePortfolio, useGameInfo, useLeaderboard, useInstruments } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Link, useNavigate } from 'react-router-dom';
import { DeadlineCountdowns } from '../components/CountdownTimer';
import { FinancialTooltip } from '../components/FinancialTooltip';
import OnboardingFlow from '../components/OnboardingFlow';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  TrendingUp, 
  TrendingDown, 
  Briefcase, 
  Award,
  Trophy, 
  Zap,
  Clock,
  AlertCircle,
  ChevronRight,
  Target
} from 'lucide-react';

const Dashboard = () => {
  const { user, token } = useAuth();
  const { portfolio, summary, validation, loading } = usePortfolio();
  const { gameInfo } = useGameInfo();
  const { myRanking, stats } = useLeaderboard();
  const { pricingStatus } = useInstruments();
  const navigate = useNavigate();
  
  // Show onboarding for new users who haven't completed it
  const [showOnboarding, setShowOnboarding] = useState(!user?.onboarding_completed);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    navigate('/portfolio');
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
  };

  const [achievements, setAchievements] = useState([]);
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/achievements`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAchievements(data.achievements || []); })
      .catch(() => {});
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-pulse text-emerald-400">Loading dashboard...</div>
      </div>
    );
  }

  const portfolioProgress = ((portfolio?.holdings?.length || 0) / 10) * 100;
  const isPortfolioComplete = validation?.valid;

  return (
    <div className="min-h-screen bg-slate-950 pb-20" data-testid="dashboard-page">
      {/* Onboarding Flow for new users */}
      {showOnboarding && (
        <OnboardingFlow 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}
      
      {/* Header */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                Welcome back, {user?.username}!
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-slate-400">
                  {gameInfo ? `Season ${gameInfo.current_season} - Week ${gameInfo.current_week}` : 'Loading...'}
                </p>
                {pricingStatus?.is_pro ? (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs" data-testid="dashboard-live-badge">
                    <span className="inline-block w-1.5 h-1.5 bg-emerald-400 rounded-full mr-1 animate-pulse"></span>
                    Live
                  </Badge>
                ) : (
                  <Badge className="bg-slate-600/20 text-slate-400 border-slate-600/30 text-xs" data-testid="dashboard-delayed-badge">
                    Delayed
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <FinancialTooltip termKey="portfolio_value">
                  <p className="text-slate-400 text-sm underline decoration-dotted decoration-slate-600 cursor-help">Portfolio Value</p>
                </FinancialTooltip>
                <p className="text-2xl font-bold text-white" data-testid="portfolio-value">
                  V${summary?.total_value_vd?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '100,000'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Countdown Timers - Mobile Optimized */}
        <div className="mb-8">
          <DeadlineCountdowns />
        </div>

        {/* Achievements Row */}
        {achievements.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-400 flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-400" />
                Achievements ({achievements.filter(a => a.unlocked).length}/{achievements.length})
              </h2>
              <Link to="/achievements" className="text-xs text-emerald-400 hover:text-emerald-300">View all</Link>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {achievements.map(ach => (
                <Link
                  key={ach.id}
                  to="/achievements"
                  className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                    ach.unlocked
                      ? 'border-amber-500/50 hover:border-amber-400'
                      : 'border-slate-700/50 opacity-40 grayscale'
                  }`}
                  title={`${ach.name}${ach.unlocked ? ' (Earned)' : ': ' + ach.description}`}
                  data-testid={`dash-ach-${ach.id}`}
                >
                  {ach.image_url ? (
                    <img src={ach.image_url} alt={ach.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${ach.unlocked ? 'bg-amber-500/20' : 'bg-slate-800'}`}>
                      <Award className={`w-5 h-5 ${ach.unlocked ? 'text-amber-400' : 'text-slate-600'}`} />
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio Status Alert */}
        {!isPortfolioComplete && (
          <div className="bg-amber-500/10 border border-amber-500/50 rounded-xl p-4 mb-8 flex items-start gap-3" data-testid="portfolio-incomplete-alert">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-400">Complete Your Portfolio</h3>
              <p className="text-slate-400 text-sm mt-1">
                {validation?.issues?.join('. ') || 'You need 10 instruments to start scoring points.'}
              </p>
              <Link to="/portfolio">
                <Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700" data-testid="complete-portfolio-btn">
                  Build Portfolio
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <FinancialTooltip termKey="holdings">
                    <p className="text-slate-400 text-sm">Holdings</p>
                  </FinancialTooltip>
                  <p className="text-2xl font-bold text-white" data-testid="holdings-count">
                    {portfolio?.holdings?.length || 0}/10
                  </p>
                </div>
                <Briefcase className="h-8 w-8 text-emerald-500/50" />
              </div>
              <Progress value={portfolioProgress} className="mt-3 h-1" />
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <FinancialTooltip termKey="gain_loss">
                    <p className="text-slate-400 text-sm">Weekly P&L</p>
                  </FinancialTooltip>
                  <p className={`text-2xl font-bold ${summary?.weekly_gain_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="weekly-pnl">
                    {summary?.weekly_gain_loss >= 0 ? '+' : ''}
                    {summary?.weekly_gain_loss_pct?.toFixed(2) || '0.00'}%
                  </p>
                </div>
                {summary?.weekly_gain_loss >= 0 ? (
                  <TrendingUp className="h-8 w-8 text-emerald-500/50" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-500/50" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <FinancialTooltip termKey="global_rank">
                    <p className="text-slate-400 text-sm underline decoration-dotted decoration-slate-600 cursor-help">Global Rank</p>
                  </FinancialTooltip>
                  <p className="text-2xl font-bold text-white" data-testid="global-rank">
                    {myRanking?.rank ? `#${myRanking.rank}` : '--'}
                  </p>
                </div>
                <Trophy className="h-8 w-8 text-amber-500/50" />
              </div>
              <p className="text-slate-500 text-xs mt-2">
                of {stats?.total_players || 0} players
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <FinancialTooltip termKey="free_trades">
                    <p className="text-slate-400 text-sm underline decoration-dotted decoration-slate-600 cursor-help">Free Trades</p>
                  </FinancialTooltip>
                  <p className="text-2xl font-bold text-white" data-testid="trades-remaining">
                    {summary?.trades_remaining_today ?? 1}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-indigo-500/50" />
              </div>
              <p className="text-slate-500 text-xs mt-2">remaining today</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Portfolio Summary */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Your Holdings</CardTitle>
                <Link to="/portfolio">
                  <Button variant="ghost" size="sm" className="text-emerald-400 hover:text-emerald-300" data-testid="view-portfolio-btn">
                    Manage Portfolio
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {portfolio?.holdings?.length > 0 ? (
                  <div className="space-y-3">
                    {portfolio.holdings.slice(0, 5).map((holding) => (
                      <div 
                        key={holding.symbol}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                        data-testid={`holding-${holding.symbol}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                            <span className="text-sm font-bold text-white">
                              {holding.symbol.slice(0, 2)}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-white">{holding.symbol}</span>
                              {holding.is_power_play && (
                                <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                  <Zap className="h-3 w-3 mr-1" />
                                  3x
                                </Badge>
                              )}
                            </div>
                            <span className="text-slate-400 text-sm">
                              {holding.units.toFixed(4)} units
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-medium">
                            V${(holding.units * holding.current_price_vd).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </p>
                          <p className={`text-sm ${
                            holding.current_price_vd >= holding.buy_price_vd ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {((holding.current_price_vd - holding.buy_price_vd) / holding.buy_price_vd * 100).toFixed(2)}%
                          </p>
                        </div>
                      </div>
                    ))}
                    {portfolio.holdings.length > 5 && (
                      <p className="text-center text-slate-500 text-sm pt-2">
                        + {portfolio.holdings.length - 5} more holdings
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Briefcase className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No holdings yet</p>
                    <Link to="/portfolio">
                      <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700" data-testid="start-building-btn">
                        Start Building
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Power Play - Hidden in simplified UI mode */}
            {!user?.simplified_ui && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-500" />
                    <FinancialTooltip termKey="power_play">
                      <span className="underline decoration-dotted decoration-slate-600 cursor-help">Power Play</span>
                    </FinancialTooltip>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {portfolio?.power_play_symbol ? (
                    <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/30">
                      <div>
                        <p className="text-slate-400 text-sm">This week's 3x pick</p>
                        <p className="text-2xl font-bold text-amber-400" data-testid="power-play-symbol">
                          {portfolio.power_play_symbol}
                        </p>
                      </div>
                      <Link to="/portfolio">
                        <Button variant="outline" size="sm" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                          Change
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-slate-800/30 rounded-xl border border-dashed border-slate-700">
                      <Target className="h-10 w-10 text-slate-600 mx-auto mb-2" />
                      <p className="text-slate-400">No Power Play selected</p>
                      <p className="text-slate-500 text-sm mt-1">Choose one holding to score 3x points</p>
                      <Link to="/portfolio">
                        <Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700" data-testid="select-power-play-btn">
                          Select Power Play
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Game Info */}
            {gameInfo && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Game Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-slate-400 text-sm">Trading Window</p>
                    <p className="text-white">{gameInfo.trading_window.open}</p>
                    <p className="text-slate-500 text-sm">to {gameInfo.trading_window.close}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Scoring Period</p>
                    <p className="text-white">{gameInfo.scoring_period.start}</p>
                    <p className="text-slate-500 text-sm">to {gameInfo.scoring_period.end}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/portfolio" className="block">
                  <Button variant="outline" className="w-full justify-start border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="quick-action-portfolio">
                    <Briefcase className="mr-2 h-4 w-4" />
                    Manage Portfolio
                  </Button>
                </Link>
                <Link to="/leaderboard" className="block">
                  <Button variant="outline" className="w-full justify-start border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="quick-action-leaderboard">
                    <Trophy className="mr-2 h-4 w-4" />
                    View Leaderboard
                  </Button>
                </Link>
                <a href="/how-to-play.html" target="_blank" className="block">
                  <Button variant="outline" className="w-full justify-start border-slate-700 text-slate-300 hover:bg-slate-800">
                    <AlertCircle className="mr-2 h-4 w-4" />
                    Game Rules
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
